import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../../styles/colors';
import SectionDecorator from '../common/SectionDecorator';

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(2); opacity: 0; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const USPSection = styled(SectionDecorator)`
  padding: 6rem 5%;
  background: ${colors.lightGrey};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: ${colors.primary};
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: ${colors.accent};
    margin: 1rem auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  padding: 2rem 0;
`;

const Card = styled.div`
  background: ${colors.white};
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.index * 0.2}s;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    
    ${props => props.index % 2 === 0 ? `
      transform: translateY(-10px) rotate(2deg);
    ` : `
      transform: translateY(-10px) rotate(-2deg);
    `}
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${colors.primary};
  color: ${colors.white};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  position: relative;
  transition: all 0.3s ease;
  
  &:before, &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: ${colors.primary};
    opacity: 0;
  }
  
  ${Card}:hover & {
    transform: scale(1.1);
    background: ${colors.accent};
    
    &:before {
      animation: ${ripple} 1.5s infinite;
    }
    
    &:after {
      animation: ${ripple} 1.5s infinite 0.5s;
    }
    
    i {
      transform: rotateY(360deg);
    }
  }
  
  i {
    transition: transform 0.6s ease;
  }
`;

const CardTitle = styled.h3`
  color: ${colors.primary};
  font-size: 1.5rem;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
  
  ${Card}:hover & {
    color: ${colors.accent};
  }
`;

const CardText = styled.p`
  color: ${colors.darkGrey};
  line-height: 1.6;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  ${Card}:hover & {
    transform: scale(1.02);
  }
`;

const USP = () => {
  const features = [
    {
      icon: "fas fa-building",
      title: "Spacious Residences",
      text: "Luxuriously designed 3 & 4 BHK homes (3BHK starting from 1260 sq. ft.) offering ample space and natural light."
    },
    {
      icon: "fas fa-map-marker-alt",
      title: "Prime Location",
      text: "Strategically located in Mulund West, offering excellent connectivity and proximity to essential services."
    },
    {
      icon: "fas fa-swimming-pool",
      title: "World-Class Amenities",
      text: "A plethora of modern amenities catering to every lifestyle need, from recreation to wellness."
    },
    {
      icon: "fas fa-award",
      title: "Trusted Developer",
      text: "A quality development by The Rudani Realty And Shree Buildcon Group, brought to you by the trusted Balrajeshwar Real Estate."
    }
  ];

  return (
    <USPSection
      id="features"
      theme="light"
      density="sparse"
    >
      <Container>
        <Title>Why Choose Codename Forresta</Title>
        <Grid>
          {features.map((feature, index) => (
            <Card key={index} index={index}>
              <IconWrapper>
                <i className={feature.icon}></i>
              </IconWrapper>
              <CardTitle>{feature.title}</CardTitle>
              <CardText>{feature.text}</CardText>
            </Card>
          ))}
        </Grid>
      </Container>
    </USPSection>
  );
};

export default USP; 